/**
 * Frontend validation utility for Slovak birth numbers (rodné číslo)
 * Format: XXXXXX/XXXX (6 digits, slash, 4 digits)
 */

/**
 * Calculate age from Slovak rodné číslo
 */
export const calculateAgeFromRodCislo = (rodCislo: string): number | null => {
    if (!rodCislo || typeof rodCislo !== 'string') {
        return null;
    }

    const trimmed = rodCislo.trim();
    const rodCisloRegex = /^\d{6}\/\d{4}$/;

    if (!rodCisloRegex.test(trimmed)) {
        return null;
    }

    const parts = trimmed.split('/');
    const datePart = parts[0]; // YYMMDD

    const year = parseInt(datePart.slice(0, 2), 10);
    let month = parseInt(datePart.slice(2, 4), 10);
    const day = parseInt(datePart.slice(4, 6), 10);

    // Handle women's months (added 50) and other special cases
    if (month > 50) {
        month = month - 50; // Women have +50 to month
    } else if (month > 20) {
        month = month - 20; // Special cases for 21st century
    }

    // Determine the full year
    let fullYear: number;
    const currentYear = new Date().getFullYear();
    const currentYearShort = currentYear % 100;

    if (year > currentYearShort) {
        fullYear = 1900 + year;
    } else {
        fullYear = 2000 + year;
    }

    // Create birth date
    const birthDate = new Date(fullYear, month - 1, day);
    const today = new Date();

    // Calculate age
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
};

/**
 * Check if person is adult (18+) based on rodné číslo
 */
export const isAdultByRodCislo = (rodCislo: string): boolean => {
    const age = calculateAgeFromRodCislo(rodCislo);
    return age !== null && age >= 18;
};

/**
 * Validate rodné číslo for child creation (must be under 18)
 */
export const validateRodCisloForChild = (rodCislo: string): { isValid: boolean; error?: string; age?: number } => {
    // First run basic validation
    const basicValidation = validateRodCislo(rodCislo);
    if (!basicValidation.isValid) {
        return basicValidation;
    }

    // Calculate age
    const age = calculateAgeFromRodCislo(rodCislo);
    if (age === null) {
        return { isValid: false, error: 'Nie je možné vypočítať vek z rodného čísla' };
    }

    // Check if adult
    if (age >= 18) {
        return {
            isValid: false,
            error: `Osoba je už dospelá (${age} rokov). Deti musia byť mladšie ako 18 rokov.`,
            age
        };
    }

    return { isValid: true, age };
};

export const validateRodCislo = (rodCislo: string): { isValid: boolean; error?: string } => {
    if (!rodCislo || typeof rodCislo !== 'string') {
        return { isValid: false, error: 'Rodné číslo je vyžadované' };
    }

    const trimmed = rodCislo.trim();

    // Check format: XXXXXX/XXXX (exactly 6 digits, slash, 4 digits)
    const rodCisloRegex = /^\d{6}\/\d{4}$/;

    if (!rodCisloRegex.test(trimmed)) {
        return {
            isValid: false,
            error: 'Rodné číslo musí mať formát XXXXXX/XXXX (6 číslic, lomka, 4 číslice)'
        };
    }

    // Extract parts for validation
    const parts = trimmed.split('/');
    const datePart = parts[0]; // YYMMDD
    const suffix = parts[1]; // XXXX

    // Validate date part
    const year = parseInt(datePart.slice(0, 2), 10);
    let month = parseInt(datePart.slice(2, 4), 10);
    const day = parseInt(datePart.slice(4, 6), 10);

    // Handle women's months (added 50) and other special cases
    let actualMonth = month;
    if (month > 50) {
        actualMonth = month - 50; // Women have +50 to month
    } else if (month > 20) {
        actualMonth = month - 20; // Special cases for 21st century
    }

    // Validate month and day
    if (actualMonth < 1 || actualMonth > 12) {
        return { isValid: false, error: 'Neplatný mesiac v rodnom čísle' };
    }

    if (day < 1 || day > 31) {
        return { isValid: false, error: 'Neplatný deň v rodnom čísle' };
    }

    // More precise day validation based on month
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // Using 29 for Feb to be permissive
    if (day > daysInMonth[actualMonth - 1]) {
        return { isValid: false, error: 'Neplatný deň pre daný mesiac' };
    }

    const currentYearShort = new Date().getFullYear() % 100;
    const fullYear = year > currentYearShort ? 1900 + year : 2000 + year;
    const birthDate = new Date(fullYear, actualMonth - 1, day);
    if (
        birthDate.getFullYear() !== fullYear ||
        birthDate.getMonth() !== actualMonth - 1 ||
        birthDate.getDate() !== day
    ) {
        return { isValid: false, error: 'Neplatný deň pre daný mesiac' };
    }

    const fullNumber = datePart + suffix;
    const checksum = parseInt(fullNumber, 10);

    if (checksum % 11 !== 0) {
        return { isValid: false, error: 'Neplatný kontrolný súčet rodného čísla' };
    }

    return { isValid: true };
};

/**
 * Format rodné číslo to ensure correct format with slash
 * Accepts formats: XXXXXXXXXX, XXXXXX/XXXX
 * Returns: XXXXXX/XXXX format only
 */
export const formatRodCislo = (rodCislo: string): string => {
    if (!rodCislo || typeof rodCislo !== 'string') {
        return '';
    }

    // Remove any existing slashes and whitespace
    const cleaned = rodCislo.replace(/[\s/]/g, '');

    // Check if it's all digits and has correct length (10 digits only)
    if (!/^\d{10}$/.test(cleaned)) {
        return rodCislo; // Return original if not valid format
    }

    // Format with slash: XXXXXX/XXXX
    return cleaned.slice(0, 6) + '/' + cleaned.slice(6, 10);
};

/**
 * Auto-format rodCislo as user types
 * Adds slash automatically at the right position
 */
export const autoFormatRodCislo = (value: string): string => {
    // Remove any non-digit characters except slash
    const cleaned = value.replace(/[^\d/]/g, '');

    // Remove any existing slashes to start fresh
    const digitsOnly = cleaned.replace(/\//g, '');

    // Don't allow more than 10 digits
    if (digitsOnly.length > 10) {
        return value.slice(0, -1); // Return previous value
    }

    // Auto-add slash after 6th digit
    if (digitsOnly.length > 6) {
        return digitsOnly.slice(0, 6) + '/' + digitsOnly.slice(6);
    }

    return digitsOnly;
};
